import React, { useEffect } from 'react';
import SEO from '../SEO';
import seoData from '../../seoData';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircle, ArrowRight, Shield, Home, Umbrella } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const RoofRepair = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={seoData["services/roof-repair"]?.title} description={seoData["services/roof-repair"]?.description} path="/services/roof-repair" keywords={seoData["services/roof-repair"]?.keywords || []} />

      <PageHero
        badge="Core Services"
        title="Roof Coating & Waterproofing"
        description="Industrial roof repair, leak sealing, and waterproofing contractor in California. Roof membrane encapsulation, reflective roof coatings, and structural roof repair for metal, concrete, and built-up roofing systems."
        backgroundKey="roof-repair"
        buttonText="Get In Touch"
      />

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Problems We Solve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {['Roof leaks at penetrations, seams, and flashing details',
              'Membrane cracking, splitting, and UV degradation',
              'Ponding water and poor drainage on flat roofs',
              'Metal roof panel corrosion and fastener failure',
              'Energy loss through uninsulated or dark roof surfaces',
              'Deferred roof maintenance creating interior damage'].map((challenge, index) => (
              <div key={index} className="flex items-start p-4 bg-indigo-50 rounded-lg">
                <Home className="text-indigo-600 mr-3 mt-0.5 flex-shrink-0" size={20} />
                <span className="text-gray-700 text-sm">{challenge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card className="border-l-4 border-l-indigo-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Umbrella className="text-indigo-600 mr-3 flex-shrink-0" size={22} />
                  Roof Leak Repair & Sealing
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Targeted repair of roof leaks at penetrations, seams, curbs, and flashing details. Elastomeric sealants and reinforced membrane patches for permanent leak repair without full roof replacement.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-indigo-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Umbrella className="text-indigo-600 mr-3 flex-shrink-0" size={22} />
                  Membrane Encapsulation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Full roof membrane encapsulation using Belzona 3111 and elastomeric coating systems. Encapsulate aging BUR, modified bitumen, EPDM, and TPO membranes to extend roof life 10-15 years.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-indigo-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Umbrella className="text-indigo-600 mr-3 flex-shrink-0" size={22} />
                  Reflective Roof Coating
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Energy-saving reflective roof coatings that reduce cooling costs and extend membrane life. White and aluminum-reflective systems for flat commercial and industrial roofs.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-indigo-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Umbrella className="text-indigo-600 mr-3 flex-shrink-0" size={22} />
                  Metal Roof Repair & Coating
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Corrosion repair and protective coating for metal roof panels, gutters, and flashings. Seal fastener holes, repair rusted panels, and apply long-term corrosion protection.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose BCS</h2>
              <div className="space-y-3">
                {['Avoid costly full roof replacement with targeted repair and encapsulation',
                  'Extend existing roof life 10-15 years',
                  'Reduce cooling costs with reflective roof coating systems',
                  'Eliminate leaks without disrupting building operations',
                  'No tear-off waste — coat over existing membranes',
                  'Manufacturer-backed warranties up to 20 years',
                  'OSHA fall protection certified crews',
                  'Emergency leak repair available 24/7'].map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="text-green-700 mr-3 mt-0.5 flex-shrink-0" size={18} />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-blue-900 text-white p-8 rounded-lg">
              <Shield className="mb-4" size={48} />
              <h3 className="text-xl font-bold mb-4">Belzona Products We Use</h3>
              <div className="space-y-3">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 3111 (Flexible Membrane)</p>
                  <p className="text-xs text-slate-500 mt-1">Roof membrane encapsulation</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 3131 (WG Membrane)</p>
                  <p className="text-xs text-slate-500 mt-1">Waterproofing grade membrane coating</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 5811 (Immersion Grade)</p>
                  <p className="text-xs text-slate-500 mt-1">Gutter and ponding area protection</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 3121 (MR7)</p>
                  <p className="text-xs text-slate-500 mt-1">Metal roof repair and sealing</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Leaking Roof? We Fix It Without Tear-Off.</h2>
          <p className="text-blue-100 mb-8">Contact our roof coating specialists for a free roof assessment. We'll recommend the most cost-effective repair or encapsulation strategy.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => window.location.href = 'tel:+19493719948'}>
              Call (949) 371-9948
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-800" onClick={() => navigate('/#contact')}>
              Request Assessment <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RoofRepair;
